/**
 * LocationService - Manages device location for weather data.
 * Uses expo-location for current coordinates with caching.
 */

import * as Location from 'expo-location';
import AsyncStorage from '@react-native-async-storage/async-storage';

const LOCATION_CACHE_KEY = 'cached_location';
const LOCATION_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

type CachedLocation = {
  latitude: number;
  longitude: number;
  timestamp: number;
};

export class LocationService {
  private static instance: LocationService | null = null;
  private cachedLocation: CachedLocation | null = null;

  static getInstance(): LocationService {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }

  async getLocation(): Promise<{ latitude: number; longitude: number } | null> {
    // Check in-memory cache
    if (this.cachedLocation && this.isFresh(this.cachedLocation)) {
      return { latitude: this.cachedLocation.latitude, longitude: this.cachedLocation.longitude };
    }

    // Check persisted cache
    const persisted = await this.loadPersistedLocation();
    if (persisted) {
      this.cachedLocation = persisted;
      if (this.isFresh(persisted)) {
        return { latitude: persisted.latitude, longitude: persisted.longitude };
      }
    }

    // Request fresh location
    const fresh = await this.requestFreshLocation();
    if (fresh) return fresh;

    // Last resort: return stale persisted location (better than nothing for weather)
    if (persisted) {
      return { latitude: persisted.latitude, longitude: persisted.longitude };
    }

    return null;
  }

  private isFresh(location: CachedLocation): boolean {
    return Date.now() - location.timestamp < LOCATION_MAX_AGE_MS;
  }

  private async requestFreshLocation(): Promise<{ latitude: number; longitude: number } | null> {
    const { status } = await Location.requestForegroundPermissionsAsync();
    if (status !== 'granted') {
      console.warn('[LocationService] Permission denied');
      return this.returnStaleOrNull();
    }

    // Try getLastKnownPositionAsync first — works on emulators and is instant
    try {
      const lastKnown = await Location.getLastKnownPositionAsync();
      if (lastKnown) {
        return this.cacheAndReturn(lastKnown.coords.latitude, lastKnown.coords.longitude);
      }
    } catch (err) {
      console.warn('[LocationService] getLastKnownPositionAsync failed:', err);
    }

    // Fall back to getCurrentPositionAsync with a timeout
    try {
      const result = await Promise.race([
        Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Low }),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
      ]);

      if (result) {
        return this.cacheAndReturn(result.coords.latitude, result.coords.longitude);
      }
    } catch (error) {
      console.warn('[LocationService] getCurrentPositionAsync failed:', error);
    }

    return this.returnStaleOrNull();
  }

  private async cacheAndReturn(
    latitude: number,
    longitude: number,
  ): Promise<{ latitude: number; longitude: number }> {
    const cached: CachedLocation = { latitude, longitude, timestamp: Date.now() };
    this.cachedLocation = cached;
    await this.persistLocation(cached);
    return { latitude, longitude };
  }

  private returnStaleOrNull(): { latitude: number; longitude: number } | null {
    if (this.cachedLocation) {
      return { latitude: this.cachedLocation.latitude, longitude: this.cachedLocation.longitude };
    }
    // Also check persisted cache synchronously from memory
    return null;
  }

  private async loadPersistedLocation(): Promise<CachedLocation | null> {
    try {
      const raw = await AsyncStorage.getItem(LOCATION_CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private async persistLocation(location: CachedLocation): Promise<void> {
    try {
      await AsyncStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(location));
    } catch {
      // Non-critical, ignore
    }
  }
}

export const locationService = LocationService.getInstance();
